import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { salesforceApiVersionControlMetadata } from './salesforce-common-metadata';
import { format } from 'app/shared/utils/string.util';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { GlobalVariables } from 'app/services/globals';
import { BuiltInDataSourceOptionId, DataSourceOptionKind } from '@powerquery/mashup-editor-client';
import { ConnectionAuthKind, getConnectorV2Name, SalesforceAuthTypes } from 'shell-models-lib';
import { EnvironmentInjector } from '@angular/core';

const salesforceApiVersionExtraValidation = _.cloneDeep(salesforceApiVersionControlMetadata);
salesforceApiVersionExtraValidation.child.validators.push({
    type: 'pattern',
    pattern: /^(4[7-9]\.\d|[5-9]\d\.\d|[1-9]\d{2,}\.\d)$/, // only 47.0 & later are supported
    errorMessage: stringResources.Api_Version_Min_Support_ValueMessage
});
const urlFormat = 'https://<domainName>.my.salesforce.com';

export const salesforceV2Metadata = async (injector: EnvironmentInjector) => {
    const result: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.salesforceV2.linkedServiceType,
        displayName: getConnectorV2Name(stringResources.Salesforce_DisplayText, __IS_FABRIC_DPE__),
        linkedServiceNamePrefix: adfModelTypeMap.salesforce.linkedServiceType,
        linkedServiceKind: 'table',
        supportedInTrident: true,
        supportCopyJobSource: true,
        supportCopyJobSink: true,
        supportMultipleIncrementalColumns: true,
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.BuiltIn,
            id: BuiltInDataSourceOptionId.SalesforceObjects
        },
        supportedAuthKindsInTrident: [ConnectionAuthKind.OAuth2],
        notSupportDeltaLoadInMetadataDrivenSolution: true,
        supportOPDG: GlobalVariables.enableOPDG,
        icon: {
            className: 'svg-i-100x100-Salesforce'
        },
        isFirstClass: true,
        fetchTableNameFromItemPath: true,
        tableNameAlias: 'objectApiName',
        tablePreviewConvert2QueryQuotes: ['"', '"'],
        supportDatasetV2: true,
        queryAlias: 'SOQLQuery',
        supportDataFlowSource: true, // controlled by `_inlineSourceHideByFeatureFlag()` for spec test
        dataflowSource: GlobalVariables.enableSalesforceMDF
            ? [
                  {
                      type: DatasetFormatType.Salesforce,
                      settings: adfModelTypeMap.salesforceV2.dataflowSettings,
                      controls: [
                          {
                              type: 'parameterizable',
                              child: {
                                  type: 'textbox',
                                  bind: 'objectApiName',
                                  label: stringResources.TableNameDisplay_ObjectApiName,
                                  required: true,
                                  isDataFlowExpression: true,
                                  validators: [
                                      {
                                          type: 'required',
                                          errorMessage: format(
                                              stringResources.RequiredTemplate,
                                              stringResources.TableNameDisplay_ObjectApiName
                                          )
                                      }
                                  ]
                              },
                              dynamicContentTextClass: 'input-spaced',
                              showErrorIcon: false,
                              isDataFlowExpression: true
                          },
                          {
                              type: 'parameterizable',
                              child: {
                                  type: 'textbox-number',
                                  bind: 'maxRecords',
                                  label: stringResources.TextboxLabel_MaxRecords,
                                  isDataFlowExpression: true,
                                  min: 1,
                                  dataType: 'integer',
                                  validators: [
                                      {
                                          type: 'number',
                                          min: 1,
                                          errorMessage: stringResources.MaxRecords_ValidatorMinErr
                                      }
                                  ]
                              },
                              dynamicContentTextClass: 'input-spaced',
                              showErrorIcon: false,
                              isDataFlowExpression: true
                          }
                      ]
                  }
              ]
            : undefined,
        copySinkTypes: [adfModelTypeMap.salesforceV2.copySinkType],
        copySourceTypes: [adfModelTypeMap.salesforceV2.copySourceType],
        category: ['ServicesApps'],
        onetimeQueryTemplate: 'select <<field1>>, <<field2>> from <<objectName>>',
        timedQueryTemplate: 'select <<field1>>, <<field2>> from <<objectName>> \nwhere ' +
        '<<field1>> >= {ts\'@{formatDateTime(pipeline().parameters.windowStart, \'yyyy-MM-dd HH:mm:ss\' )}\'} \nAND ' +
        '<<field1>> < {ts\'@{formatDateTime(pipeline().parameters.windowEnd, \'yyyy-MM-dd HH:mm:ss\' )}\'}',
        deltaLoadQueryTemplate: `select * from <<tableName>>
        where <<columnName>> > @{if(contains(json(item().DataLoadingBehaviorSettings).watermarkColumnType, 'Int'),
        json(item().DataLoadingBehaviorSettings).watermarkColumnStartValue,
        concat('{ts''', formatDateTime(json(item().DataLoadingBehaviorSettings).watermarkColumnStartValue, 'yyyy-MM-dd HH:mm:ss'), '''}'))}
        and <<columnName>> <= @{if(contains(json(item().DataLoadingBehaviorSettings).watermarkColumnType, 'Int'),
        activity('GetMaxWatermarkValue').output.firstRow.CurrentMaxWaterMarkColumnValue,
        concat('{ts''', formatDateTime(activity('GetMaxWatermarkValue').output.firstRow.CurrentMaxWaterMarkColumnValue, 'yyyy-MM-dd HH:mm:ss'), '''}'))}`,
        supportFirstClassParameterExperience: true,
        controls: [
            {
                type: 'parameterizable',
                child: {
                    type: 'textbox',
                    bind: 'environmentUrl',
                    label: stringResources.ModelName_EnvironmentUrl,
                    required: true,
                    isLabelVertical: true,
                    placeholder: urlFormat,
                    validators: [
                        { type: 'required', errorMessage: '' },
                        {
                            type: 'pattern',
                            pattern: `^https://(.)+(\\.my\\.salesforce\\.)`,
                            errorMessage: format(stringResources.Validator_ErrorMessage_Pattern, urlFormat)
                        }
                    ]
                }
            },
            {
                type: 'select',
                bind: 'authenticationType',
                label: stringResources.ModelName_AuthenticationType,
                isLabelVertical: true,
                options: [
                    {
                        label: stringResources.AuthenticationType_OAuth2ClientCredential,
                        value: SalesforceAuthTypes.OAuth
                    }
                ],
                defaultValue: SalesforceAuthTypes.OAuth,
                labelPosition: 'left'
            },
            {
                type: 'parameterizable',
                child: {
                    type: 'textbox',
                    bind: 'clientId',
                    label: stringResources.ModelName_ClientId,
                    required: true,
                    validators: [{ type: 'required', errorMessage: '' }],
                    isLabelVertical: true
                }
            },
            {
                type: 'dynamic-akv-switcher',
                enableAKVParameterization: true,
                bind: 'clientSecret',
                label: stringResources.ModelName_ClientSecret,
                child: {
                    type: 'parameterizable',
                    attribute: 'clientSecret',
                    disableAddParameter: true,
                    child: {
                        type: 'password',
                        label: stringResources.ModelName_ClientSecret,
                        required: true,
                        isLabelVertical: true
                    }
                },
                description: format(
                    stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                    format(stringResources.ModelName_LinkedServiceTemplate, getConnectorV2Name(stringResources.Salesforce_DisplayText, __IS_FABRIC_DPE__)),
                    stringResources.ModelName_ClientSecret
                ),
                validators: [{ type: 'required', errorMessage: '' }]
            },
            salesforceApiVersionExtraValidation
        ]
    };
    return result;
};
